/*
species-photos.json loader — mirrors Android SpeciesPhotos.kt.
Manifeste des photos de référence par espèce (sprints S9/S10 Android,
`tools/build_dataset.py` bloc `_write_species_photos`) : racine objet
`{meta: {v, licenses, sources}, photos: {"<sk>": [{f,r,src,lic,by,u}]}}`.
Les 777 WebP vivent sous /data/species-photos/ (SW cache-first /data/,
jamais précachés — chargement lazy à l'ouverture d'une fiche).

L'attribution (src/lic/by/u) est portée par chaque photo, mais toutes les
photos d'une même espèce la partagent par construction (cascades S9 Paris
et S10 Wikidata/iNaturalist disjointes par espèce) — les consommateurs
lisent celle de la principale.

Consommateurs : SpeciesScreen (hero mosaïque + lightbox référence),
ArboretumScreen / GenreDetailScreen (vignettes catalogue anti-spoiler),
PhotoCreditsScreen (crédits par source).
*/

import { fetchJson } from './fetchJson'

const kAssetPath = '/data/species-photos.json'
const kSupportedVersion = 1  // meta.v du manifeste

/* Une photo de référence. `role` : 'p' = principale (hero), 'd' = détail. */
export interface ReferencePhoto
{
	f: string  // nom de fichier WebP (« 12-0.webp »)
	role: 'p' | 'd'
	src: string  // clé vers meta.sources (« paris », « wikimedia-commons », « inaturalist »)
	lic: string  // clé vers meta.licenses (« odbl-1.0», « cc0», « pd », « cc-by »)
	by: string  // auteur / attribution texte
	u: string | null  // URL de la page source, null si absente
}

/* Jeu de photos d'une espèce : une principale + 0..3 détails. */
export interface SpeciesPhotos
{
	principal: ReferencePhoto
	details: ReferencePhoto[]
	all: ReferencePhoto[]  // principale d'abord, puis les détails dans l'ordre du manifest
}

export interface PhotoLicense
{
	name: string
	url: string
}

export interface PhotoSourceMeta
{
	name: string
	authors: string  // "" pour les sources sans crédit collectif (wikimedia, inaturalist)
}

interface RawPhoto
{
	f: string
	r?: string
	src: string
	lic: string
	by: string
	u?: string | null
}

interface RawManifest
{
	meta?: {
		v?: number
		licenses?: Record<string, PhotoLicense>
		sources?: Record<string, PhotoSourceMeta>
	}
	photos?: Record<string, RawPhoto[]>
}

let fBySk: Map<number, SpeciesPhotos> | null = null  // memoized sk -> photos
let fLicenses: Map<string, PhotoLicense> = new Map()
let fSources: Map<string, PhotoSourceMeta> = new Map()
let fLoading: Promise<void> | null = null  // dedup concurrent callers

/*
Ensure the manifest is loaded. Idempotent. Asset absent ou illisible →
tables vides (parité SpeciesPhotoRepository.load Android : la fiche espèce
retombe sur son hero texte, le catalogue sur les thumbs capture).
*/
export async function ensureSpeciesPhotos(): Promise<void>
{
	if (fBySk) return
	if (fLoading) return fLoading
	fLoading = (async () => {
		let vRaw: RawManifest
		try
		{
			vRaw = await fetchJson<RawManifest>(kAssetPath)
		}
		catch
		{
			fBySk = new Map()
			fLoading = null
			return
		}
		if (vRaw.meta?.v !== kSupportedVersion)
		{
			console.warn(`${kAssetPath}: version ${String(vRaw.meta?.v)} inattendue (attendu ${kSupportedVersion}) — photos de référence ignorées`)
			fBySk = new Map()
			fLoading = null
			return
		}
		fLicenses = new Map(Object.entries(vRaw.meta.licenses ?? {}))
		fSources = new Map(Object.entries(vRaw.meta.sources ?? {}))
		const vMap = new Map<number, SpeciesPhotos>()
		for (const [vSkStr, vList] of Object.entries(vRaw.photos ?? {}))
		{
			const vSk = parseInt(vSkStr, 10)
			if (!Number.isFinite(vSk) || !Array.isArray(vList) || vList.length === 0) continue
			const vPhotos = vList.map(parsePhoto)
			// Principale = 1er élément marqué "p" ; fallback = premier élément.
			// Exclusion par identité : dans le cas fallback la principale a le
			// rôle 'd' et serait sinon dupliquée dans `all` (parité Kotlin).
			const vPrincipal = vPhotos.find((inPhoto) => inPhoto.role === 'p') ?? vPhotos[0]
			const vDetails = vPhotos.filter((inPhoto) => inPhoto !== vPrincipal && inPhoto.role === 'd')
			vMap.set(vSk, { principal: vPrincipal, details: vDetails, all: [vPrincipal, ...vDetails] })
		}
		fBySk = vMap
		fLoading = null
	})()
	return fLoading
}

function parsePhoto(inRaw: RawPhoto): ReferencePhoto
{
	return {
		f: inRaw.f,
		role: inRaw.r === 'p' ? 'p' : 'd',
		src: inRaw.src,
		lic: inRaw.lic,
		by: inRaw.by,
		u: typeof inRaw.u === 'string' && inRaw.u.length > 0 ? inRaw.u : null,
	}
}

/*
Photos d'une espèce. Null si non chargé, espèce non illustrée, ou manifeste
absent — les consommateurs retombent sur leur rendu texte/capture.
*/
export function photosFor(inSk: number): SpeciesPhotos | null
{
	return fBySk?.get(inSk) ?? null
}

export function licenseFor(inKey: string): PhotoLicense | null
{
	return fLicenses.get(inKey) ?? null
}

export function sourceFor(inKey: string): PhotoSourceMeta | null
{
	return fSources.get(inKey) ?? null
}

/* Vue complète pour l'écran Crédits photos : sk → photos, ordre du manifest. */
export function allSpeciesPhotos(): ReadonlyMap<number, SpeciesPhotos>
{
	return fBySk ?? new Map()
}

/* URL servie par le SW (cache-first /data/) pour une photo de référence. */
export function photoUrl(inPhoto: ReferencePhoto): string
{
	return `/data/species-photos/${inPhoto.f}`
}
